import { Box, Container, styled, Tab, Tabs } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { DefaultSeoProps, NextSeoProps } from "next-seo";

import ShopLayout1 from "components/layouts/ShopLayout1";
import Navbar from "components/navbar/Navbar";
import ProductDescription from "components/products/ProductDescription";
import ProductIntro from "components/products/ProductIntro";
import modules from "modules";
import { useAppDispatch } from "hooks/use-app-dispatch";
import {
  GetProductDetailsInitialResponse,
  GetProductDetailsResponse,
  GetProductHasStocksResponse,
  GetProductImagesResponse,
  ProdictHasStocksData,
  ProductDetailsInitialType,
  ProductDetailsType,
} from "modules/product-details/types";
import { CategoriesIdsEnum } from "common";
import CompatibleMotherboardCarousel from "components/products/CompatibleMotherboardCarousel";
import ProductNotFound from "components/products/ProductNotFound";

import ProductOpenGraphTags from "components/products/ProductOpenGraphTags";
import ProductSchemaOrgTags from "components/products/ProductSchemaOrgTags";
import ProductJSONLDSchemaOrgTags from "components/products/ProductJSONLDSchemaOrgTags";
import BuildsGalleryCarousel from "components/products/BuildsGalleryCarousel";
import { useBuildsGallery } from "hooks/use-builds-gallery";
import { fbqTrack } from "utils/FacebookMetaPixel";
import { viewContent } from "modules/meta-pixel/constants";
import { buildsGalleryCarouselFlag } from "common/constants/feature-toggles";
import { unwrapResult } from "@reduxjs/toolkit";
import AlternativeProductCarousel from "components/products/AlternativeProductsCarousel";
import { AvailableBranchesData } from "modules/products/types";
import { GetStaticProps, GetStaticPaths } from "next";
import { useBranchSelection } from "hooks/use-branch-selection";
import { useIsMount } from "hooks/use-is-mount";
import { getPoweredBuildsThunk, PcBundleData } from "modules/pc-bundle";
import ProductBundlesCarousel from "components/products/ProductBundlesCarousel";
import { H1 } from "components/Typography";

const { actions: productDetailsActions, ProductDetailsApi } =
  modules.productDetails;

const { actions: recentlyViewedActions } = modules.recentlyViewed;

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 0,
  marginTop: 80,
  marginBottom: 24,
  borderBottom: `1px solid ${theme.palette.text.disabled}`,
  "& .inner-tab": {
    minHeight: 40,
    fontWeight: 600,
    textTransform: "capitalize",
  },
}));

// ===============================================================
type ProductDetailsProps = {
  productDetails?: ProductDetailsType;
  productImages?: string[];
  seoProps?: DefaultSeoProps;
};
// ===============================================================

const ProductDetailsPage: FC<ProductDetailsProps> = ({
  productDetails,
  productImages,
}) => {
  const {
    buildsItemOnProduct,
    isBuildsGalleryLoading,
    getBuildsGalleryOnProducts,
  } = useBuildsGallery();
  const isFirstMount = useIsMount();

  const dispatch = useAppDispatch();
  const router = useRouter();
  const { selectedBranch } = useBranchSelection();

  const previousBranch = useRef(null);
  const previousProductId = useRef(null);

  const [productAvailableBranches, setProductAvailableBranches] =
    useState<AvailableBranchesData>();

  const [hasStocks, setHasStocks] = useState<ProdictHasStocksData>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [localDetails, setLocalDetails] =
    useState<ProductDetailsType>(productDetails);
  const [productBundles, setProductBundles] = useState<PcBundleData[]>([]);
  const [isBundlesLoading, setIsBundlesLoading] = useState<boolean>(false);

  // Did not use `useMemo` for SEO purposes
  const productImageLink = productImages?.[0] || "";

  const onLoadTrack = useCallback(async () => {
    // NOTE: we are sure next line is not string[]
    // @ts-ignore:next-line
    const product_uuid: string = router?.query?.q || "";
    if (product_uuid) {
      fbqTrack(viewContent, {
        content_ids: [product_uuid],
        content_type: "product",
      });
    }
  }, [router]);

  const showCompatibleMobos = useMemo(() => {
    if (
      localDetails?.category_id === CategoriesIdsEnum.CPU ||
      localDetails?.category === "CPU"
    ) {
      return true;
    }
    return false;
  }, [localDetails]);

  const getProductPoweredBuilds = useCallback(
    async (uuid: string = "") => {
      if (!uuid) {
        console.error("No product uuid found for powered builds");
      }
      setIsBundlesLoading(true);

      const res = unwrapResult(await dispatch(getPoweredBuildsThunk(uuid)));
      if (res.success) {
        setProductBundles(res.originalData.data || []);
      } else {
        console.error("Failed to get powered builds");
      }
      setIsBundlesLoading(false);
    },
    [dispatch]
  );

  const getAvailabilityBranchesOfProduct = useCallback(
    async (uuid?: string) => {
      try {
        const response = unwrapResult(
          await dispatch(
            productDetailsActions.getAvailabilityBranchOfProductsThunk(uuid)
          )
        );

        if (response?.success) {
          setProductAvailableBranches(response?.originalData?.data);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch]
  );

  const getProductStocksStatus = useCallback(
    async (branch_id?: number) => {
      setIsLoading(true);
      const res = await ProductDetailsApi.getProductHasStocks({
        product_uuid: productDetails?.product_uuid,
        branch_id,
      });
      const { success, data } = res?.data as GetProductHasStocksResponse;

      if (success) {
        setIsLoading(false);
        getAvailabilityBranchesOfProduct(productDetails?.product_uuid);
        setHasStocks(data);
      } else {
        setIsLoading(false);
        console.error("Failed to get product stocks status");
      }
    },
    [getAvailabilityBranchesOfProduct, productDetails?.product_uuid]
  );

  const handleData = useCallback(
    async (branch_id?: number) => {
      // const { product_slug, product_uuid } = localDetails;
      if (localDetails?.product_slug && localDetails?.product_uuid) {
        dispatch(
          recentlyViewedActions.addItemToList({
            ...localDetails,
            slug: localDetails?.product_slug,
          })
        );
        getBuildsGalleryOnProducts(localDetails?.product_slug);
        getProductPoweredBuilds(localDetails?.product_uuid);
        getProductStocksStatus(branch_id);
      } else {
        console.error("Empty product slug value");
      }
    },
    [
      dispatch,
      getBuildsGalleryOnProducts,
      getProductPoweredBuilds,
      getProductStocksStatus,
      localDetails,
    ]
  );

  const getInitialDetails = useCallback(
    async (slug: string) => {
      const res = unwrapResult(
        await dispatch(
          productDetailsActions.getProductInitialDetailThunk({
            product_slug: slug,
          })
        )
      );

      if (res.success) {
        const { retail_price } = res.originalData.data;
        setLocalDetails({ ...localDetails, retail_price });
      }
    },
    [dispatch, localDetails]
  );

  useEffect(() => {
    setLocalDetails(productDetails); //reload local state
  }, [productDetails]);

  useEffect(() => {
    if (
      previousBranch.current != selectedBranch ||
      previousProductId.current != productDetails?.product_uuid
    ) {
      handleData(selectedBranch?.id);
      previousBranch.current = selectedBranch;
      previousProductId.current = productDetails?.product_uuid;
    }
  }, [handleData, productDetails?.product_uuid, selectedBranch]);

  useEffect(() => {
    router.query.q && onLoadTrack();
  }, [onLoadTrack, router]);

  useEffect(() => {
    const { id: slug } = router.query;
    isFirstMount && slug && getInitialDetails(slug as string);
  }, [getInitialDetails, isFirstMount, router]);

  if (router?.isFallback) {
    return (
      <ShopLayout1 navbar={<Navbar />}>
        <H1>Loading...</H1>
      </ShopLayout1>
    );
  }

  if (!localDetails?.product_name) {
    return (
      <ShopLayout1 navbar={<Navbar />}>
        <ProductNotFound />
      </ShopLayout1>
    );
  }

  return (
    <ShopLayout1 navbar={<Navbar />}>
      {/* PIXEL MICRO TAGS */}
      <div id="meta-pixel-tags">
        <ProductOpenGraphTags
          productImageLink={productImageLink}
          productDetails={localDetails}
        />
        <ProductSchemaOrgTags
          productImageLink={productImageLink}
          productDetails={localDetails}
        />
        <ProductJSONLDSchemaOrgTags
          productImageLink={productImageLink}
          productDetails={localDetails}
        />
      </div>
      <Container sx={{ my: window.innerWidth === 768 ? 6 : 4 }}>
        <ProductIntro
          product={localDetails}
          hasStocks={hasStocks?.has_stocks}
          stocksLeft={hasStocks?.stocks_left}
          productImagesProp={productImages}
          availableBranches={productAvailableBranches}
          isLoading={isLoading}
        />

        {productBundles[0] && (
          <ProductBundlesCarousel
            productBundles={productBundles}
            isBundlesLoading={isBundlesLoading}
          />
        )}

        <AlternativeProductCarousel
          productName={localDetails?.product_name}
          productCategory={localDetails?.category}
          current_product_id={localDetails?.product_uuid}
        />
        {showCompatibleMobos ? (
          <CompatibleMotherboardCarousel
            withBundle={localDetails?.with_bundle}
            cpuProductName={localDetails?.product_name}
            compatibleMotherboards={localDetails?.compatible_mobos}
          />
        ) : null}
        {buildsGalleryCarouselFlag && buildsItemOnProduct.length > 0 ? (
          <BuildsGalleryCarousel
            buildsItems={buildsItemOnProduct}
            buildsGalleryLoading={isBuildsGalleryLoading}
            productName={localDetails?.product_name}
          />
        ) : null}
        <StyledTabs
          value={0}
          textColor="primary"
          indicatorColor="primary"
          // onChange={handleOptionClick}
        >
          <Tab className="inner-tab" label="Description" />
          {/* <Tab className="inner-tab" label="Review (3)" /> */}
        </StyledTabs>
        <Box mb={6}>
          <ProductDescription
            references={localDetails?.references || []}
            warrantyDuration={localDetails?.warranty_duration}
            description={localDetails?.description_html_url}
            productName={localDetails?.product_name}
          />
          {/* {selectedOption === 1 && <ProductReview />} */}
        </Box>
      </Container>
    </ShopLayout1>
  );
};

// ISR: store visited page to static cache meaning faster loading next visit

//--fetch data from endpoints and compile into props--//
export const getStaticProps: GetStaticProps = async (context) => {
  const { id } = context.params as { id: string };

  let productDetails: ProductDetailsType & ProductDetailsInitialType = {};
  let productImages: string[] = [];

  // retain initial details call here for seo title and initial image props
  console.time('initialDetailsRes')
  const initialDetailsRes = (
    await ProductDetailsApi.getProductDetailsInitial({
      product_slug: id,
    })
  )?.data as GetProductDetailsInitialResponse;
  console.log(initialDetailsRes)
  console.timeEnd('initialDetailsRes')
  

  console.time('fullDetailsRes')
  const fullDetailsRes = (
    await ProductDetailsApi.getProductDetails({
      product_slug: id,
    })
  ).data as GetProductDetailsResponse;
  console.log(fullDetailsRes)
  console.timeEnd('fullDetailsRes')
  

  console.time('productImagesRes')
  const productImagesRes = (
    await ProductDetailsApi.getProductImages({
      product_slug: id,
    })
  ).data as GetProductImagesResponse;
  console.log(productImagesRes)
  console.timeEnd('productImagesRes')

  if (initialDetailsRes?.success && fullDetailsRes?.success) {
    productDetails = {
      ...initialDetailsRes?.data,
      ...fullDetailsRes?.data,
      product_name: initialDetailsRes?.data?.title,
      product_slug: id,
    };

    productImages = [initialDetailsRes?.data?.image_url];
  } else {
    console.error("Failed to get product details");
  }

  if (productImagesRes?.success && productImagesRes?.data?.images[0]) {
    productImages = [...productImages, ...productImagesRes?.data.images];
  } else {
    console.error("Failed to get product images");
  }

  const openGraphImages = [
    {
      url: productDetails?.image_url || "/assets/images/kachi/kachi-happy.png",
      width: 500,
      height: 500,
      alt: productDetails?.title || id,
    },
  ];

  const seoProps: DefaultSeoProps & NextSeoProps = {
    title: productDetails?.title || "PC Worth",
    description: productDetails?.title || id,
    canonical: `https://pcworth.com/product/${id}?q=${productDetails?.product_slug}`,
    openGraph: {
      url: `https://pcworth.com/product/${id}?q=${productDetails?.product_slug}`,
      title: productDetails?.title || "PC Worth",
      description: productDetails?.title || id,
      images: openGraphImages,
    },
  };

  return {
    props: { productDetails, productImages, seoProps },
    // revalidate: 86400, // 24 hrs in seconds interval to update data
    revalidate: 300,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    // Recommended to include best-seller product slugs or other popular products paths. (eg: paths: [{params: {id: radeon-gpu}}])
    // Slugs in path are stored automatically on build so they will be faster when visited the first time cause they dont need to load and store.
    paths: [],
    fallback: true, // wait for page to load if not yet cached 'true' needs a loading page and 'false' will result in 404
  };
};

export default ProductDetailsPage;
